import RichTextEditor from '@ui/Editor2'
import parse from 'html-react-parser'
import { useId } from 'react'
import type { ReactQuillProps } from 'react-quill'
import './templateTableCellEditor.css'

const cellFormats = ['bold', 'italic', 'underline', 'color', 'background']

const cellModules = {
  clipboard: {
    matchVisual: false
  },
  history: {
    delay: 500,
    maxStack: 50,
    userOnly: true
  },
  toolbar: {
    container: [['bold', 'italic', 'underline'], [{ color: [] }]]
  }
}

export type TemplateTableCellEditorProps = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  className?: string
  /** Passed through for native form validation when nested in a form. */
  required?: boolean
  name?: string
}

/**
 * Compact Quill editor for lab/imaging template table cells.
 * Supports multi-line text, bold (Ctrl/Cmd+B), and text color.
 */
const TemplateTableCellEditor = ({
  value,
  onChange,
  placeholder = 'Enter value',
  className = '',
  required,
  name
}: TemplateTableCellEditorProps) => {
  const reactId = useId()
  const fieldId = name ? `table-cell-${name}` : `table-cell-${reactId}`

  const handleChange: ReactQuillProps['onChange'] = (html, _delta, source) => {
    if (source !== 'user') return
    onChange(html === '<p><br></p>' ? '' : html)
  }

  return (
    <div className={`template-table-cell-editor ${className}`.trim()}>
      <RichTextEditor
        borderRadius="6px"
        className="template-table-cell-quill"
        formats={cellFormats}
        id={fieldId}
        minHeight="52px"
        module={cellModules}
        onChange={handleChange}
        placeholder={placeholder}
        theme="snow"
        value={value || ''}
      />
      {required ? (
        <input
          aria-hidden
          className="sr-only"
          name={name}
          onChange={() => undefined}
          required
          tabIndex={-1}
          value={stripHtml(value) ? '1' : ''}
        />
      ) : null}
    </div>
  )
}

function stripHtml(html: string | undefined): string {
  if (!html) return ''
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim()
}

/** Read-only Quill/HTML cell content for fill + preview surfaces. */
export function TemplateTableCellHtml({ html, className = '' }: { html: string; className?: string }) {
  return (
    <div className={`template-table-cell-html text-sm leading-snug text-[#333] ${className}`.trim()}>
      {html ? parse(html) : '\u00A0'}
    </div>
  )
}

export default TemplateTableCellEditor
